'use client';

import { Switch as RadixSwitch } from 'radix-ui';
import { type ComponentProps, type ReactNode, useId } from 'react';
import { cn, focusRing } from './cn';

/** A setting applied as soon as it is flipped. */
export function Switch({
  checked,
  onCheckedChange,
  label,
  description,
  aside,
  disabled,
  className,
  id: idProp,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  /** Beside the label but outside it, such as a help button: a label holds no other control. */
  aside?: ReactNode;
  disabled?: boolean;
  className?: string;
  /** Id of the switch, for a label or a test that points at it. */
  id?: string;
}) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const descriptionId = `${id}-description`;
  return (
    <div className={cn('flex items-center justify-between gap-4', className)}>
      {/* The description stays out of the label, so the switch is named by the label alone. */}
      <div className="min-w-0">
        {aside ? (
          <div className="flex items-center gap-1.5">
            <label htmlFor={id} className="text-crow-body text-crow-text">
              {label}
            </label>
            {aside}
          </div>
        ) : (
          <label htmlFor={id} className="block text-crow-body text-crow-text">
            {label}
          </label>
        )}
        {description ? (
          <p id={descriptionId} className="mt-0.5 text-crow-label text-crow-text-3">
            {description}
          </p>
        ) : null}
      </div>
      <SwitchControl
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        aria-describedby={description ? descriptionId : undefined}
      />
    </div>
  );
}

/**
 * The switch alone, for a row that shows its label elsewhere: give it `aria-label`. Other button
 * attributes (`tabIndex`, `aria-hidden`, `name`…) go to the switch.
 */
export function SwitchControl({
  checked,
  onCheckedChange,
  disabled,
  id,
  className,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  ...props
}: Omit<
  ComponentProps<typeof RadixSwitch.Root>,
  'checked' | 'onCheckedChange' | 'defaultChecked' | 'children'
> & {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  id?: string;
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
}) {
  return (
    <RadixSwitch.Root
      {...props}
      id={id}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      className={cn(
        'relative h-[18px] w-[30px] shrink-0 rounded-full bg-crow-input transition-colors duration-[var(--crow-dur-1)] disabled:opacity-50 data-[state=checked]:bg-crow-accent',
        focusRing,
        className,
      )}
    >
      {/* The knob stays white in both states; the fine shadow keeps it visible on light accents. */}
      <RadixSwitch.Thumb className="block size-3.5 translate-x-0.5 rounded-full bg-white shadow-[0_0_0_0.5px_rgb(0_0_0/0.12),0_1px_2px_rgb(0_0_0/0.3)] transition-transform duration-[var(--crow-dur-1)] data-[state=checked]:translate-x-[14px]" />
    </RadixSwitch.Root>
  );
}
